import { useSearchParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppShell } from '../components/ui';
import StudyHeader from '../components/study/StudyHeader';
import StudyMix from '../components/study/mix/StudyMix';
import ActiveSessionsGrid from '../components/study/ActiveSessionsGrid';
import SessionHistoryTable from '../components/study/SessionHistoryTable';
import StudyAnalytics from '../components/study/StudyAnalytics';

const TAB_KEYS = ['launcher', 'active', 'history', 'analytics'] as const;

type TabKey = (typeof TAB_KEYS)[number];

function isValidTab(value: string | null): value is TabKey {
  return TAB_KEYS.includes(value as TabKey);
}

export default function StudyControlCenter() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  // Backward compat: redirect ?sessionId=X to /study/session/X
  const legacySessionId = searchParams.get('sessionId');
  if (legacySessionId) {
    return <Navigate to={`/study/session/${legacySessionId}`} replace />;
  }

  const tabParam = searchParams.get('tab');
  const activeTab: TabKey = isValidTab(tabParam) ? tabParam : 'launcher';

  const handleTabChange = (tab: TabKey) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'launcher') {
      params.delete('tab');
    } else {
      params.set('tab', tab);
    }
    setSearchParams(params);
  };

  return (
    <AppShell maxWidth="7xl">
      <StudyHeader />

        {/* Tab bar */}
        <nav
          className="mt-6 flex gap-1 overflow-x-auto rounded-2xl border border-slate-200/70 bg-slate-50/60 p-1.5 dark:border-white/10 dark:bg-white/5"
          role="tablist"
          aria-label={t('study.control.tabsLabel')}
        >
          {TAB_KEYS.map((tabKey) => (
            <button
              key={tabKey}
              role="tab"
              aria-selected={activeTab === tabKey}
              aria-controls={`panel-${tabKey}`}
              onClick={() => handleTabChange(tabKey)}
              className={`relative flex-shrink-0 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-all rounded-xl
                ${
                  activeTab === tabKey
                    ? 'bg-blue-600 text-white shadow-[0_6px_16px_-6px_rgba(37,99,235,0.6)]'
                    : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white dark:hover:bg-white/10'
                }`}
            >
              {t(`study.control.tabs.${tabKey}`)}
            </button>
          ))}
        </nav>

        {/* Tab panels */}
        <div className="mt-6">
          <TabPanel id="panel-launcher" active={activeTab === 'launcher'}>
            <StudyMix />
          </TabPanel>

          <TabPanel id="panel-active" active={activeTab === 'active'}>
            <ActiveSessionsGrid />
          </TabPanel>

          <TabPanel id="panel-history" active={activeTab === 'history'}>
            <SessionHistoryTable />
          </TabPanel>

          <TabPanel id="panel-analytics" active={activeTab === 'analytics'}>
            <StudyAnalytics />
          </TabPanel>
        </div>
    </AppShell>
  );
}

function TabPanel({
  id,
  active,
  children,
}: {
  id: string;
  active: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      id={id}
      role="tabpanel"
      tabIndex={0}
      hidden={!active}
      className={active ? undefined : 'hidden'}
    >
      {active && children}
    </div>
  );
}

